import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, CheckCircle2, Sparkles } from "lucide-react";
import { Reveal, Stagger, staggerItem } from "@/components/Motion";
import { motion } from "framer-motion";
import TechMarquee from "@/components/TechMarquee";
import CtaBanner from "@/components/CtaBanner";
import Seo from "@/components/Seo";
import { SERVICES, STATS, TESTIMONIALS, IMAGES } from "@/lib/site-data";

export default function Home() {
  return (
    <div data-testid="page-home">
      <Seo
        title="Cutting Edge Technologies Consulting | IT Consulting, Training & Staffing — Houston, TX & Washington, DC"
        description="IT Consulting, Training & Certification, Managed Services, and Tech Staffing serving Houston, Richmond, Sugar Land, Fulshear TX and Washington, DC. Modernize, upskill, and hire tech talent."
        path="/"
      />
      {/* HERO */}
      <section className="relative min-h-[100vh] bg-slate-950 text-white overflow-hidden pt-20">
        <div className="absolute inset-0">
          <img
            src={IMAGES.hero}
            alt="Modern glass technology tower"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/85 to-slate-950/30" />
          <div className="absolute inset-0 grid-lines-dark opacity-70" />
        </div>

        <div className="relative mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 pt-16 md:pt-28 pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-8">
              <Reveal>
                <div className="flex items-center gap-3">
                  <span className="h-px w-10 bg-emerald-500" />
                  <span className="eyebrow text-emerald-400">Cutting Edge Technologies · Global</span>
                </div>
              </Reveal>
              <Reveal delay={0.08}>
                <h1 className="mt-7 font-display text-5xl md:text-6xl lg:text-7xl font-semibold leading-[1.02] tracking-tight">
                  Empowering businesses through{" "}
                  <span className="relative inline-block">
                    <span className="relative z-10 text-emerald-400">technology</span>
                    <span className="absolute -bottom-2 left-0 right-0 h-2 bg-emerald-500/30" />
                  </span>
                  , talent, and training.
                </h1>
              </Reveal>
              <Reveal delay={0.18}>
                <p className="mt-8 text-lg md:text-xl text-slate-300 max-w-2xl leading-relaxed">
                  A consulting partner for Tech and IT — we modernize stacks, upskill teams, and place
                  vetted tech talent so ambitious organizations can ship faster and focus on running the business.
                </p>
              </Reveal>
              <Reveal delay={0.28}>
                <div className="mt-12 flex flex-wrap items-center gap-4">
                  <Link
                    to="/contact"
                    data-testid="hero-get-started"
                    className="group inline-flex items-center gap-2 bg-emerald-500 text-slate-950 px-8 py-4 font-medium hover:bg-emerald-400 transition-colors"
                  >
                    Get Started
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" strokeWidth={1.75} />
                  </Link>
                  <Link
                    to="/contact"
                    data-testid="hero-book-consultation"
                    className="inline-flex items-center gap-2 border border-slate-700 text-white px-8 py-4 font-medium hover:border-emerald-500 hover:text-emerald-400 transition-colors"
                  >
                    Book a Consultation
                  </Link>
                </div>
              </Reveal>
            </div>

            {/* Right column — framed stat card */}
            <div className="lg:col-span-4 lg:pt-16">
              <Reveal delay={0.36}>
                <div className="relative">
                  <div className="absolute inset-0 border border-emerald-500 translate-x-3 translate-y-3" />
                  <div className="relative bg-slate-900/70 backdrop-blur-md border border-slate-800 p-8">
                    <div className="flex items-center gap-2 text-emerald-400">
                      <Sparkles className="w-4 h-4" strokeWidth={1.5} />
                      <span className="eyebrow">Why Cutting Edge Technologies</span>
                    </div>
                    <p className="mt-4 text-white font-display text-2xl leading-snug">
                      One partner for strategy, delivery, training, and talent.
                    </p>
                    <div className="mt-8 grid grid-cols-2 gap-6">
                      {STATS.map((s) => (
                        <div key={s.label}>
                          <div className="font-display text-3xl font-semibold text-white">{s.value}</div>
                          <div className="mt-1 text-xs text-slate-400">{s.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>

          {/* Scroll hint */}
          <Reveal delay={0.5}>
            <div className="mt-20 flex items-center gap-3 text-slate-500 font-mono text-[11px] tracking-[0.3em] uppercase">
              <span className="h-px w-8 bg-slate-700" />
              Scroll to explore
            </div>
          </Reveal>
        </div>
      </section>

      {/* Marquee */}
      <TechMarquee theme="dark" />

      {/* SERVICES OVERVIEW — Bento Grid */}
      <section className="py-28 md:py-36 bg-slate-50">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-16">
            <div className="lg:col-span-7">
              <Reveal>
                <div className="eyebrow text-emerald-600">What we do</div>
                <h2 className="mt-4 font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-slate-900 leading-[1.05]">
                  Four practices. One integrated partner.
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-5">
              <Reveal delay={0.1}>
                <p className="text-slate-600 text-lg leading-relaxed">
                  Consulting, training, managed services, and staffing — engineered to work together
                  so your technology investment compounds.
                </p>
              </Reveal>
            </div>
          </div>

          <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-5">
            {SERVICES.map((s, i) => {
              const Icon = s.icon;
              // Bento spans: 01 wide, 02 narrow, 03 narrow, 04 wide
              const span =
                i === 0 || i === 3
                  ? "lg:col-span-7"
                  : "lg:col-span-5";
              return (
                <motion.div key={s.slug} variants={staggerItem} className={`${span} md:col-span-1`}>
                  <Link
                    to={`/services#${s.slug}`}
                    data-testid={`home-service-card-${s.slug}`}
                    className="group block bg-white border border-slate-200 p-8 lg:p-10 h-full hover:border-slate-900 transition-all hover:-translate-y-1 hover:shadow-xl relative overflow-hidden"
                  >
                    <div className="flex items-start justify-between gap-6">
                      <div>
                        <div className="font-mono text-xs text-slate-400 tracking-widest">{s.code} / SERVICE</div>
                        <div className="mt-6 inline-flex items-center justify-center h-12 w-12 bg-slate-900 text-emerald-400 group-hover:bg-emerald-500 group-hover:text-slate-950 transition-colors">
                          <Icon className="w-5 h-5" strokeWidth={1.5} />
                        </div>
                        <h3 className="mt-6 font-display text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight">
                          {s.title}
                        </h3>
                        <div className="eyebrow mt-2 text-slate-500">{s.tag}</div>
                        <p className="mt-5 text-slate-600 leading-relaxed max-w-lg">{s.description}</p>
                      </div>
                      <ArrowUpRight className="w-6 h-6 text-slate-400 group-hover:text-emerald-600 transition-all group-hover:translate-x-1 group-hover:-translate-y-1" strokeWidth={1.5} />
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </Stagger>
        </div>
      </section>

      {/* WHY CHOOSE Cutting Edge Technologies */}
      <section className="py-28 md:py-36 bg-white">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <Reveal>
              <div className="eyebrow text-emerald-600">Why Cutting Edge Technologies</div>
              <h2 className="mt-4 font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-slate-900 leading-[1.05]">
                A partner that moves at the speed of your roadmap.
              </h2>
              <p className="mt-6 text-slate-600 text-lg leading-relaxed max-w-xl">
                We blend consulting discipline with engineering velocity. That means executive-grade
                strategy on Monday and shipped infrastructure by Friday — backed by a trained talent
                bench that scales with you.
              </p>
            </Reveal>
            <div className="mt-10 space-y-5">
              {[
                "Senior and Lead practitioners only — no bait-and-switch staffing",
                "Fixed-outcome engagement models, transparent pricing",
                "AI, cloud, DevOps, Cybersecurity & data — deep specialist bench",
                "Training that produces hireable engineers, not certificates",
              ].map((item, i) => (
                <Reveal key={item} delay={0.1 + i * 0.06}>
                  <div className="flex items-start gap-4">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" strokeWidth={1.5} />
                    <span className="text-slate-700">{item}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <div className="lg:col-span-6 order-1 lg:order-2">
            <Reveal delay={0.1}>
              <div className="relative">
                <div className="absolute -inset-6 bg-slate-100 -z-10" />
                <div className="absolute inset-0 border border-emerald-500 translate-x-4 translate-y-4" />
                <img
                  src={IMAGES.aboutTeam}
                  alt="Cutting Edge Technologies team collaborating in a modern office"
                  className="relative w-full h-[520px] object-cover"
                />
                <div className="relative bg-slate-950 text-white p-6 max-w-sm -mt-10 ml-6">
                  <div className="eyebrow text-emerald-400">Engagement model</div>
                  <div className="mt-2 font-display text-xl">From strategy call to deployed value — in weeks, not quarters.</div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS PREVIEW */}
      <section className="py-28 md:py-36 bg-slate-950 text-white relative overflow-hidden">
        <div className="absolute inset-0 grid-lines-dark opacity-50" />
        <div className="relative mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-14">
            <div className="lg:col-span-7">
              <Reveal>
                <div className="eyebrow text-emerald-400">Signal, not noise</div>
                <h2 className="mt-4 font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]">
                  Trusted by operators who ship.
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-5 lg:pb-4">
              <Reveal delay={0.1}>
                <Link to="/testimonials" className="arrow-link text-emerald-400 font-medium" data-testid="home-testimonials-link">
                  Read all stories <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                </Link>
              </Reveal>
            </div>
          </div>

          <Stagger className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {TESTIMONIALS.slice(0, 3).map((t, i) => (
              <motion.figure
                key={i}
                variants={staggerItem}
                className="bg-slate-900/60 border border-slate-800 p-8 flex flex-col justify-between hover:border-emerald-500/60 transition-colors"
                data-testid={`home-testimonial-${i}`}
              >
                <blockquote className="font-display text-xl leading-snug text-white">
                  &ldquo;{t.quote}&rdquo;
                </blockquote>
                <figcaption className="mt-8 pt-6 border-t border-slate-800">
                  <div className="font-medium text-white">{t.name}</div>
                  <div className="text-sm text-slate-400 mt-1">{t.role} · {t.company}</div>
                </figcaption>
              </motion.figure>
            ))}
          </Stagger>
        </div>
      </section>

      <CtaBanner />
    </div>
  );
}
